import React, { useState, useEffect } from 'react';
import { Patient } from '../../../services/firebase/FirebasePatientService';
import { FirebaseInquiryService, Inquiry as FirestoreInquiry } from '../../../services/firebase/FirebaseInquiryService';
import { FirebaseInviteService } from '../../../services/firebase/FirebaseInviteService';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';
import { useConfirm } from '../../../hooks/useConfirm';
import ShareIntakeModal from './ShareIntakeModal';
import InquiryDetailPanel from './InquiryDetailPanel';

interface Inquiry {
  id: string;
  name: string;
  age: number;
  contact: {
    primaryName: string;
    primaryPhone: string;
    primaryEmail: string;
    secondaryName?: string;
    secondaryPhone?: string;
    secondaryEmail?: string;
  };
  billingType: 'Insurance' | 'Self-pay';
  status: 'Scheduled' | 'Documents completed' | 'No upcoming appointments' | 'Contact info missing';
  dateAdded: Date;
  hasAppointment?: boolean;
  documentsCompleted?: boolean;
  clientNotes?: string;
}

const InquiriesTab: React.FC = () => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date-newest');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showShareIntakeModal, setShowShareIntakeModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);


  // Create Prospective Client Form State
  const [createForm, setCreateForm] = useState({
    clientType: 'Adult' as 'Adult' | 'Minor' | 'Couple',
    currentTab: 'client' as 'client' | 'contact' | 'client1' | 'client2',
    legalFirstName: '',
    legalLastName: '',
    preferredName: '',
    dateOfBirth: { month: '', day: '', year: '' },
    billingTypeIndividual: 'Self-pay' as 'Self-pay' | 'Insurance',
    billingTypeGroup: 'Self-pay' as 'Self-pay' | 'Insurance',
    billingType: 'Self-pay' as 'Self-pay' | 'Insurance', // For Couple
    responsibleForBilling: false,
    referredBy: '',
    addToWaitlist: false,
    location: 'Video Office',
    emails: [] as Array<{ email: string; type: string; permission: string }>,
    phones: [] as Array<{ phone: string; type: string; permission: string }>,
    reminderUpcomingAppointments: { enabled: false, email: 'No email', phone: 'No phone', textEnabled: false, voiceEnabled: false },
    reminderIncompleteDocuments: { enabled: false, email: 'No email', phone: 'No phone', textOnly: true },
    reminderCancellations: { enabled: false, email: 'No email', phone: 'No phone', textEnabled: false, voiceEnabled: false }
  });

  useEffect(() => {
    loadInquiries();
  }, []);

  const loadInquiries = async () => {
    if (!user?.uid) return;
    setIsLoading(true);
    try {
      const firestoreInquiries = await FirebaseInquiryService.getInquiries(user.uid);
      // Map Firestore inquiries to local Inquiry interface
      const mapped: Inquiry[] = firestoreInquiries.map((fi) => {
        const name = `${fi.legalFirstName} ${fi.legalLastName}`;
        const age = fi.dateOfBirth
          ? Math.floor((Date.now() - new Date(fi.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
          : 0;
        const statusMap: Record<string, Inquiry['status']> = {
          'new': 'No upcoming appointments',
          'intake_sent': 'No upcoming appointments',
          'registered': 'Documents completed',
          'active': 'Scheduled',
          'archived': 'No upcoming appointments',
        };
        return {
          id: fi.id,
          name,
          age,
          contact: fi.contact,
          billingType: fi.billingType,
          status: statusMap[fi.status] || 'No upcoming appointments',
          dateAdded: fi.createdAt,
          hasAppointment: fi.status === 'active',
          documentsCompleted: fi.status === 'registered' || fi.status === 'active',
          clientNotes: fi.notes,
        };
      });
      setInquiries(mapped);
    } catch (error) {
      console.error('Error loading inquiries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!user?.uid) return;
    try {
      const fullName = `${createForm.legalFirstName} ${createForm.legalLastName}`.trim();
      const primaryEmail = createForm.emails[0]?.email || '';
      const primaryPhone = createForm.phones[0]?.phone || '';

      await FirebaseInquiryService.createInquiry({
        providerId: user.uid,
        legalFirstName: createForm.legalFirstName,
        legalLastName: createForm.legalLastName,
        preferredName: createForm.preferredName || undefined,
        dateOfBirth: createForm.dateOfBirth.year
          ? `${createForm.dateOfBirth.year}-${createForm.dateOfBirth.month.padStart(2, '0')}-${createForm.dateOfBirth.day.padStart(2, '0')}`
          : undefined,
        email: primaryEmail,
        phone: primaryPhone,
        billingType: createForm.billingTypeIndividual,
        status: 'new',
        clientType: createForm.clientType,
        referredBy: createForm.referredBy || undefined,
        contact: {
          primaryName: fullName,
          primaryPhone,
          primaryEmail,
        },
      });

      setShowCreateModal(false);
      resetCreateForm();
      await loadInquiries();
    } catch (error: any) {
      console.error('Error creating inquiry:', error);
      toast.error('Error', error.message || 'Failed to create prospective client.');
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      clientType: 'Adult',
      currentTab: 'client',
      legalFirstName: '',
      legalLastName: '',
      preferredName: '',
      dateOfBirth: { month: '', day: '', year: '' },
      billingTypeIndividual: 'Self-pay',
      billingTypeGroup: 'Self-pay',
      billingType: 'Self-pay',
      responsibleForBilling: false,
      referredBy: '',
      addToWaitlist: false,
      location: 'Video Office',
      emails: [],
      phones: [],
      reminderUpcomingAppointments: { enabled: false, email: 'No email', phone: 'No phone', textEnabled: false, voiceEnabled: false },
      reminderIncompleteDocuments: { enabled: false, email: 'No email', phone: 'No phone', textOnly: true },
      reminderCancellations: { enabled: false, email: 'No email', phone: 'No phone', textEnabled: false, voiceEnabled: false }
    });
  };

  const addEmail = () => {
    setCreateForm({
      ...createForm,
      emails: [...createForm.emails, { email: '', type: 'Personal', permission: 'Send' }]
    });
  };

  const addPhone = () => {
    setCreateForm({
      ...createForm,
      phones: [...createForm.phones, { phone: '', type: 'Mobile', permission: 'Send' }]
    });
  };

  const handleOpenDetail = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setShowDetailModal(true);
  };

  const handleSaveInquiry = (updatedInquiry: Inquiry) => {
    setInquiries(prev =>
      prev.map(inq => inq.id === updatedInquiry.id ? updatedInquiry : inq)
    );
    toast.success('Success', `${updatedInquiry.name} has been updated successfully.`);
  };

  const handleShareIntake = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setShowActionMenu(null);
    setShowShareIntakeModal(true);
  };

  const handleShareComplete = async (selectedContacts: string[], selectedDocs: string[], emailBody: string) => {
    if (!user?.uid || !selectedInquiry) return;

    try {
      const providerName = user.displayName || userProfile?.displayName || 'Provider';
      const practiceName = userProfile?.clinicId || 'Your Practice';

      // Generate invite token
      const inviteId = await FirebaseInviteService.generateInvite({
        providerId: user.uid,
        providerName,
        practiceName,
        inquiryId: selectedInquiry.id,
        clientEmail: selectedInquiry.contact.primaryEmail,
        clientName: selectedInquiry.name,
        selectedDocuments: selectedDocs,
      });

      // Update inquiry status
      await FirebaseInquiryService.updateInquiry(selectedInquiry.id, {
        status: 'intake_sent',
      });

      // Build the invite URL
      const link = FirebaseInviteService.getInviteUrl(inviteId);
      setInviteLink(link);

      // Copy to clipboard
      try {
        await navigator.clipboard.writeText(link);
      } catch (e) {
        // Clipboard may fail in some browsers
      }

      // Open mailto with the email body + invite link
      const clientEmail = selectedInquiry.contact.primaryEmail;
      const subject = encodeURIComponent(`You're invited to register — ${practiceName}`);
      const body = encodeURIComponent(`${emailBody}\n\nRegister here: ${link}`);
      window.open(`mailto:${clientEmail}?subject=${subject}&body=${body}`, '_self');

      toast.success('Invite Link Copied', 'Invite link generated and copied to clipboard. Your email client should open with the pre-composed message.');

      await loadInquiries();
    } catch (error: any) {
      console.error('Error generating invite:', error);
      toast.error('Error', error.message || 'Failed to generate invite. Please try again.');
    }
  };

  const handleScheduleAppointment = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setShowActionMenu(null);
    setShowScheduleModal(true);
  };

  const handleSendReferrals = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setShowActionMenu(null);
    // TODO: Implement send referrals functionality
    toast.info('Coming Soon', `Send referrals for ${inquiry.name}. This will allow you to refer this client to other providers or resources.`);
  };

  const handleMakeActive = async (inquiry: Inquiry) => {
    setShowActionMenu(null);
    const confirmActive = await confirm({
      title: 'Make Active Client',
      message: `Convert ${inquiry.name} to an active client? This will move them from inquiries to your active client list.`,
      confirmLabel: 'Make Active',
      variant: 'info'
    });

    if (confirmActive) {
      try {
        await FirebaseInquiryService.updateInquiry(inquiry.id, { status: 'active' });
        await loadInquiries();
        toast.success('Status Updated', `${inquiry.name} is now an active client.`);
      } catch (error) {
        console.error('Error making client active:', error);
        toast.error('Error', 'Failed to update client status.');
      }
    }
  };

  const handleArchiveClient = async (inquiry: Inquiry) => {
    setShowActionMenu(null);
    const confirmArchive = await confirm({
      title: 'Archive Client',
      message: `Archive ${inquiry.name}? Archived clients can be restored later if needed.`,
      confirmLabel: 'Archive',
      variant: 'warning'
    });

    if (confirmArchive) {
      try {
        await FirebaseInquiryService.archiveInquiry(inquiry.id);
        await loadInquiries();
        toast.success('Client Archived', `${inquiry.name} has been archived.`);
      } catch (error) {
        console.error('Error archiving client:', error);
        toast.error('Error', 'Failed to archive client.');
      }
    }
  };

  const handleDeleteClient = async (inquiry: Inquiry) => {
    setShowActionMenu(null);
    const confirmDelete = await confirm({
      title: 'Delete Client',
      message: `Delete ${inquiry.name}? This action cannot be undone. The client record will be permanently removed.`,
      confirmLabel: 'Delete',
      variant: 'danger'
    });

    if (confirmDelete) {
      try {
        await FirebaseInquiryService.deleteInquiry(inquiry.id);
        await loadInquiries();
        toast.success('Client Deleted', `${inquiry.name} has been deleted.`);
      } catch (error) {
        console.error('Error deleting inquiry:', error);
        toast.error('Error', 'Failed to delete client.');
      }
    }
  };

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#16325B',
      minHeight: '100vh',
      color: '#f9fafb'
    }}>
      {/* Info Banner */}
      <div style={{
        backgroundColor: '#dbeafe',
        border: '1px solid #3b82f6',
        borderLeft: '4px solid #3b82f6',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        color: '#1e40af',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <span style={{ fontSize: '20px' }}>ℹ️</span>
        <div>
          <strong>Keeping track of contact form inquiries just got easier</strong>
          <p style={{ margin: '4px 0 0 0' }}>
            Spend less time digging through emails. Now you can view and respond to messages from the MyClinicCore contact form from your inquiries list.{' '}
            <span style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>Learn about the contact form.</span>
          </p>
        </div>
        <button
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            color: '#1e40af',
            marginLeft: 'auto'
          }}
        >
          ✕
        </button>
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '600',
            color: '#f9fafb',
            margin: '0 0 8px 0'
          }}>
            Inquiries
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#d1d5db',
            margin: 0
          }}>
            Manage inquiries and intake for new clients
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Add prospective client
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '200px',
            maxWidth: '300px',
            padding: '10px 12px',
            backgroundColor: '#374151',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            color: '#f9fafb',
            fontSize: '14px'
          }}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            backgroundColor: '#374151',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            color: '#f9fafb',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">≡ All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="documents-completed">Documents completed</option>
          <option value="no-appointments">No upcoming appointments</option>
          <option value="contact-missing">Contact info missing</option>
        </select>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            backgroundColor: '#374151',
            border: '1px solid #4b5563',
            borderRadius: '6px',
            color: '#f9fafb',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="all">≡ All inquiry sources</option>
          <option value="contact-form">Contact Form</option>
          <option value="phone">Phone</option>
          <option value="email">Email</option>
          <option value="referral">Referral</option>
        </select>

        <button
          onClick={loadInquiries}
          style={{
            padding: '10px 16px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#3b82f6',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Reset
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: '#d1d5db' }}>Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '10px 12px',
              backgroundColor: '#374151',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              color: '#f9fafb',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="date-newest">Date added (Newest)</option>
            <option value="date-oldest">Date added (Oldest)</option>
            <option value="name">Name</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      {/* Inquiries Table */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 2fr 1fr 1.5fr 1fr 100px',
          gap: '16px',
          padding: '16px',
          backgroundColor: '#1e3a5f',
          borderBottom: '1px solid #374151',
          fontWeight: '600',
          fontSize: '14px',
          color: '#d1d5db'
        }}>
          <div>Name</div>
          <div>Contact</div>
          <div>Billing type</div>
          <div>Status</div>
          <div>Date added</div>
          <div>Actions</div>
        </div>

        {/* Table Body */}
        {inquiries.map((inquiry) => (
          <div
            key={inquiry.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 1fr 1.5fr 1fr 100px',
              gap: '16px',
              padding: '16px',
              borderBottom: '1px solid #374151',
              alignItems: 'flex-start'
            }}
          >
            {/* Name */}
            <div>
              <div
                onClick={() => handleOpenDetail(inquiry)}
                style={{
                  fontWeight: '600',
                  color: '#3b82f6',
                  marginBottom: '4px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {inquiry.name} <span style={{ fontWeight: 'normal', color: '#d1d5db' }}>(age {inquiry.age})</span>
              </div>
              {inquiry.contact.secondaryName && (
                <div style={{ color: '#e5e7eb', fontSize: '14px' }}>
                  {inquiry.contact.secondaryName}
                </div>
              )}
            </div>

            {/* Contact */}
            <div style={{ fontSize: '14px' }}>
              <div style={{ color: '#e5e7eb', marginBottom: '4px' }}>
                {inquiry.contact.primaryName}
              </div>
              {inquiry.contact.primaryPhone && (
                <div style={{ color: '#d1d5db' }}>
                  {inquiry.contact.primaryPhone}
                </div>
              )}
              {inquiry.contact.primaryEmail && (
                <div style={{ color: '#d1d5db' }}>
                  {inquiry.contact.primaryEmail}
                </div>
              )}
              {inquiry.contact.secondaryName && (
                <>
                  <div style={{ color: '#e5e7eb', marginTop: '8px' }}>
                    {inquiry.contact.secondaryName}
                  </div>
                  {inquiry.contact.secondaryPhone && (
                    <div style={{ color: '#d1d5db' }}>
                      {inquiry.contact.secondaryPhone}
                    </div>
                  )}
                  {inquiry.contact.secondaryEmail && (
                    <div style={{ color: '#d1d5db' }}>
                      {inquiry.contact.secondaryEmail}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Billing Type */}
            <div style={{ color: '#e5e7eb' }}>
              {inquiry.billingType}
            </div>

            {/* Status */}
            <div>
              {inquiry.status === 'Scheduled' && (
                <div>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    Scheduled
                  </span>
                  {inquiry.documentsCompleted && (
                    <div style={{ color: '#10b981', fontSize: '13px' }}>
                      Documents completed
                    </div>
                  )}
                </div>
              )}
              {inquiry.status === 'No upcoming appointments' && (
                <div style={{ color: '#d1d5db', fontSize: '14px' }}>
                  No upcoming appointments
                </div>
              )}
              {inquiry.status === 'Contact info missing' && (
                <div style={{ color: '#f59e0b', fontSize: '14px' }}>
                  Contact info missing
                </div>
              )}
            </div>

            {/* Date Added */}
            <div style={{ color: '#e5e7eb' }}>
              {inquiry.dateAdded.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
              {inquiry.dateAdded.toLocaleDateString() === new Date().toLocaleDateString() && (
                <div style={{ fontSize: '12px', color: '#d1d5db' }}>via contact form</div>
              )}
            </div>

            {/* Actions */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowActionMenu(showActionMenu === inquiry.id ? null : inquiry.id)}
                style={{
                  padding: '8px',
                  backgroundColor: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#f9fafb',
                  fontSize: '16px'
                }}
              >
                ⋯
              </button>

              {showActionMenu === inquiry.id && (
                <div style={{
                  position: 'absolute',
                  right: 0,
                  top: '40px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  zIndex: 100,
                  minWidth: '220px',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  <button
                    onClick={() => handleShareIntake(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Share intake documents
                  </button>
                  <button
                    onClick={() => handleScheduleAppointment(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Schedule appointment
                  </button>
                  <button
                    onClick={() => handleSendReferrals(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Send Referrals
                  </button>

                  {/* Divider */}
                  <div style={{
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    margin: '4px 0'
                  }} />

                  <button
                    onClick={() => handleOpenDetail(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    View client details
                  </button>
                  <button
                    onClick={() => handleMakeActive(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Make client active
                  </button>
                  <button
                    onClick={() => handleArchiveClient(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1e293b',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Archive client
                  </button>
                  <button
                    onClick={() => handleDeleteClient(inquiry)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#ef4444',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Delete client
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Prospective Client Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: '20px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                margin: 0,
                color: '#1e293b',
                fontSize: '24px',
                fontWeight: '600'
              }}>
                Create prospective client
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            {/* Scrollable Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Client Info Section */}
              <div>
                <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#1e293b' }}>Client info</h3>

                {/* Client Type */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Client type
                  </label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {['Adult', 'Minor', 'Couple'].map(type => (
                      <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="clientType"
                          value={type}
                          checked={createForm.clientType === type}
                          onChange={(e) => setCreateForm({ ...createForm, clientType: e.target.value as any, currentTab: e.target.value === 'Minor' ? 'client' : e.target.value === 'Couple' ? 'client1' : 'client' })}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span style={{ color: '#1e293b', fontSize: '14px' }}>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Tab Navigation for Minor and Couple */}
                {(createForm.clientType === 'Minor' || createForm.clientType === 'Couple') && (
                  <div style={{ marginBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', gap: '0' }}>
                      {createForm.clientType === 'Minor' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setCreateForm({ ...createForm, currentTab: 'client' })}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderBottom: createForm.currentTab === 'client' ? '3px solid #3b82f6' : '3px solid transparent',
                              color: createForm.currentTab === 'client' ? '#3b82f6' : '#64748b',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Client
                          </button>
                          <button
                            type="button"
                            onClick={() => setCreateForm({ ...createForm, currentTab: 'contact' })}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderBottom: createForm.currentTab === 'contact' ? '3px solid #3b82f6' : '3px solid transparent',
                              color: createForm.currentTab === 'contact' ? '#3b82f6' : '#64748b',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Contact
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setCreateForm({ ...createForm, currentTab: 'client1' })}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderBottom: createForm.currentTab === 'client1' ? '3px solid #3b82f6' : '3px solid transparent',
                              color: createForm.currentTab === 'client1' ? '#3b82f6' : '#64748b',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Client 1
                          </button>
                          <button
                            type="button"
                            onClick={() => setCreateForm({ ...createForm, currentTab: 'client2' })}
                            style={{
                              padding: '12px 24px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              borderBottom: createForm.currentTab === 'client2' ? '3px solid #3b82f6' : '3px solid transparent',
                              color: createForm.currentTab === 'client2' ? '#3b82f6' : '#64748b',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Client 2
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Add Existing Client Button for Couple */}
                {createForm.clientType === 'Couple' && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      type="button"
                      style={{
                        padding: '10px 16px',
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Add Existing Client or Contact
                    </button>
                  </div>
                )}

                {/* Name Fields */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      *Legal first name
                    </label>
                    <input
                      type="text"
                      value={createForm.legalFirstName}
                      onChange={(e) => setCreateForm({ ...createForm, legalFirstName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#f8fafc',
                        color: '#1e293b',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      *Legal last name
                    </label>
                    <input
                      type="text"
                      value={createForm.legalLastName}
                      onChange={(e) => setCreateForm({ ...createForm, legalLastName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#f8fafc',
                        color: '#1e293b',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {/* Preferred Name & DOB */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      What name do they go by? ℹ️
                    </label>
                    <input
                      type="text"
                      value={createForm.preferredName}
                      onChange={(e) => setCreateForm({ ...createForm, preferredName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#f8fafc',
                        color: '#1e293b',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                      Date of birth
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: '8px' }}>
                      <select
                        value={createForm.dateOfBirth.month}
                        onChange={(e) => setCreateForm({ ...createForm, dateOfBirth: { ...createForm.dateOfBirth, month: e.target.value } })}
                        style={{
                          padding: '10px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">MM</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                          <option key={m} value={m.toString().padStart(2, '0')}>{m.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select
                        value={createForm.dateOfBirth.day}
                        onChange={(e) => setCreateForm({ ...createForm, dateOfBirth: { ...createForm.dateOfBirth, day: e.target.value } })}
                        style={{
                          padding: '10px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">DD</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d.toString().padStart(2, '0')}>{d.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select
                        value={createForm.dateOfBirth.year}
                        onChange={(e) => setCreateForm({ ...createForm, dateOfBirth: { ...createForm.dateOfBirth, year: e.target.value } })}
                        style={{
                          padding: '10px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">YYYY</option>
                        {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Responsible for Billing Checkbox (Couple only) */}
                {createForm.clientType === 'Couple' && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={createForm.responsibleForBilling}
                        onChange={(e) => setCreateForm({ ...createForm, responsibleForBilling: e.target.checked })}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ color: '#1e293b', fontSize: '14px' }}>Responsible for billing</span>
                    </label>
                  </div>
                )}

                {/* Billing Type */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Billing type
                  </label>

                  {createForm.clientType === 'Couple' ? (
                    // Couple: Single billing type
                    <div style={{ display: 'flex', gap: '16px' }}>
                      {['Self-pay', 'Insurance'].map(type => (
                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="billing"
                            value={type}
                            checked={createForm.billingType === type}
                            onChange={(e) => setCreateForm({ ...createForm, billingType: e.target.value as any })}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ color: '#1e293b', fontSize: '14px' }}>{type}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    // Adult/Minor: Individual and Group billing
                    <>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>For individual appointments</div>
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                        {['Self-pay', 'Insurance'].map(type => (
                          <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="billingIndividual"
                              value={type}
                              checked={createForm.billingTypeIndividual === type}
                              onChange={(e) => setCreateForm({ ...createForm, billingTypeIndividual: e.target.value as any })}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ color: '#1e293b', fontSize: '14px' }}>{type}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>For group appointments</div>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        {['Self-pay', 'Insurance'].map(type => (
                          <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="billingGroup"
                              value={type}
                              checked={createForm.billingTypeGroup === type}
                              onChange={(e) => setCreateForm({ ...createForm, billingTypeGroup: e.target.value as any })}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ color: '#1e293b', fontSize: '14px' }}>{type}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Referred By */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Referred by
                  </label>
                  <select
                    value={createForm.referredBy}
                    onChange={(e) => setCreateForm({ ...createForm, referredBy: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select</option>
                    <option value="friend">Friend</option>
                    <option value="family">Family</option>
                    <option value="doctor">Doctor</option>
                    <option value="online">Online Search</option>
                    <option value="insurance">Insurance Directory</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Waitlist */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createForm.addToWaitlist}
                      onChange={(e) => setCreateForm({ ...createForm, addToWaitlist: e.target.checked })}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ color: '#1e293b', fontSize: '14px' }}>Add to waitlist</span>
                  </label>
                </div>
              </div>

              {/* Clinician Section */}
              <div>
                <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#1e293b' }}>Clinician</h3>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#475569', fontWeight: '500' }}>
                    Location
                  </label>
                  <select
                    value={createForm.location}
                    onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#f8fafc',
                      color: '#1e293b',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="Video Office">Video Office</option>
                    <option value="Main Office">Main Office</option>
                  </select>
                </div>
              </div>

              {/* Contact Details Section */}
              <div>
                <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#1e293b' }}>Contact details</h3>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                  Manage contact info for reminders and notifications. An email is needed for granting Client Portal access.
                </p>

                {/* Email Section */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                    <div>Email</div>
                    <div>Type</div>
                    <div>Permission</div>
                  </div>
                  {createForm.emails.map((email, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="email"
                        value={email.email}
                        onChange={(e) => {
                          const newEmails = [...createForm.emails];
                          newEmails[idx].email = e.target.value;
                          setCreateForm({ ...createForm, emails: newEmails });
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                      <select
                        value={email.type}
                        onChange={(e) => {
                          const newEmails = [...createForm.emails];
                          newEmails[idx].type = e.target.value;
                          setCreateForm({ ...createForm, emails: newEmails });
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option>Personal</option>
                        <option>Work</option>
                      </select>
                      <select
                        value={email.permission}
                        onChange={(e) => {
                          const newEmails = [...createForm.emails];
                          newEmails[idx].permission = e.target.value;
                          setCreateForm({ ...createForm, emails: newEmails });
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option>Send</option>
                        <option>Do not send</option>
                      </select>
                    </div>
                  ))}
                  <button
                    onClick={addEmail}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#3b82f6',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      textAlign: 'left'
                    }}
                  >
                    + Add email
                  </button>
                </div>

                {/* Phone Section */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                    <div>Phone</div>
                    <div>Type</div>
                    <div>Permission</div>
                  </div>
                  {createForm.phones.map((phone, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="tel"
                        value={phone.phone}
                        onChange={(e) => {
                          const newPhones = [...createForm.phones];
                          newPhones[idx].phone = e.target.value;
                          setCreateForm({ ...createForm, phones: newPhones });
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      />
                      <select
                        value={phone.type}
                        onChange={(e) => {
                          const newPhones = [...createForm.phones];
                          newPhones[idx].type = e.target.value;
                          setCreateForm({ ...createForm, phones: newPhones });
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option>Mobile</option>
                        <option>Home</option>
                        <option>Work</option>
                      </select>
                      <select
                        value={phone.permission}
                        onChange={(e) => {
                          const newPhones = [...createForm.phones];
                          newPhones[idx].permission = e.target.value;
                          setCreateForm({ ...createForm, phones: newPhones });
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: '#f8fafc',
                          color: '#1e293b',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        <option>Send</option>
                        <option>Do not send</option>
                      </select>
                    </div>
                  ))}
                  <button
                    onClick={addPhone}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#3b82f6',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      textAlign: 'left'
                    }}
                  >
                    + Add phone
                  </button>
                </div>

                {/* Reminder Options - Simplified for now */}
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '16px' }}>
                  <strong>Reminder and notification options</strong>
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                      <span>Upcoming appointments</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                      <span>Incomplete documents</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="checkbox" style={{ width: '16px', height: '16px' }} />
                      <span>Cancellations</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateClient}
                disabled={!createForm.legalFirstName || !createForm.legalLastName}
                style={{
                  padding: '12px 24px',
                  backgroundColor: createForm.legalFirstName && createForm.legalLastName ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: createForm.legalFirstName && createForm.legalLastName ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Add Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Appointment Modal */}
      {showScheduleModal && selectedInquiry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2001
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #6CA6C1',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#f9fafb',
              fontSize: '20px'
            }}>
              Schedule Appointment
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Client</div>
              <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 'bold' }}>
                {selectedInquiry.name}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                Appointment Date
              </label>
              <input
                type="date"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '6px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                Appointment Time
              </label>
              <input
                type="time"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '6px'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                Appointment Type
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '6px'
                }}
              >
                <option>Initial Consultation</option>
                <option>Follow-up Session</option>
                <option>Therapy Session</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedInquiry(null);
                  toast.info('Coming Soon', `Appointment scheduling for ${selectedInquiry.name} is not yet available.`);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Schedule
              </button>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedInquiry(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Intake Modal */}
      {showShareIntakeModal && selectedInquiry && (
        <ShareIntakeModal
          isOpen={showShareIntakeModal}
          onClose={() => {
            setShowShareIntakeModal(false);
            setSelectedInquiry(null);
          }}
          clientName={selectedInquiry.name}
          contacts={[
            {
              id: 'primary',
              name: selectedInquiry.contact.primaryName,
              initials: selectedInquiry.contact.primaryName.split(' ').map(n => n[0]).join('').toUpperCase(),
              email: selectedInquiry.contact.primaryEmail,
              phone: selectedInquiry.contact.primaryPhone,
              hasPortalAccess: true
            },
            ...(selectedInquiry.contact.secondaryName ? [{
              id: 'secondary',
              name: selectedInquiry.contact.secondaryName,
              initials: selectedInquiry.contact.secondaryName.split(' ').map(n => n[0]).join('').toUpperCase(),
              email: selectedInquiry.contact.secondaryEmail || '',
              phone: selectedInquiry.contact.secondaryPhone || '',
              hasPortalAccess: true
            }] : [])
          ]}
          onShare={handleShareComplete}
        />
      )}

      {/* Inquiry Detail Panel */}
      {showDetailModal && selectedInquiry && (
        <InquiryDetailPanel
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedInquiry(null);
          }}
          inquiry={selectedInquiry}
          onSave={handleSaveInquiry}
        />
      )}
      {ConfirmDialog}
    </div>
  );
};

export default InquiriesTab;
